/** Livestock box trailer backed to the stock race. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

/**
 * The trailer's collision-relevant local geometry, in the asset's own axes.
 *
 * The box body, wheels and legs are authored together below, but two rooms park
 * this asset differently. Keeping these values here means a re-proportion of the
 * running gear cannot leave beat 6 with wheels and beat 7 with a walk-through
 * drawing. Each room still owns the transform from these local axes to its own
 * world axes: the yard is askew; the hedgerow is square-on.
 */
export const BOX_TRAILER_RUNNING_GEAR = {
  /** `deck-frame` is 0.12 m deep around y = 1.09, so daylight ends at 1.03. */
  deckY: 1.03,
  /** The four visible tyres, including their axle locations and physical envelope. */
  wheels: { radius: 0.5, width: 0.32, centreX: 1.115, axleZ: [-2.2, -3.4] },
  /** The two visible landing legs. Their square section is collision-relevant. */
  legs: { z: 2.2, section: 0.12, top: 1.15, centreX: 0.85, halfX: 0.91, halfZ: 0.06 },
  /** Coarse AABB around the two wheel axles, read from the wheel geometry below. */
  bogie: { z: -2.8, halfX: 1.275, halfZ: 1.1, top: 1, axleZ: [-2.2, -3.4] },
} as const;

function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const item = new THREE.Mesh(geometry, material);
  item.name = name;
  item.castShadow = true;
  item.receiveShadow = true;
  return item;
}

function box(
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const item = mesh(new THREE.BoxGeometry(...size), material, name);
  item.position.set(...position);
  return item;
}

function frontNoseFairing(): THREE.BufferGeometry {
  // The sloped wedge cannot be a box; explicit indexed geometry preserves the
  // fairing instead of replacing it with a flat front when the core is blocked in.
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(
      new Float32Array([
        -1.225, 3.51, 3.7,
        -1.225, 3.5, 4,
        -1.225, 3.9, 3.7,
        1.225, 3.51, 3.7,
        1.225, 3.5, 4,
        1.225, 3.9, 3.7,
      ]),
      3,
    ),
  );
  geometry.setIndex([
    0, 1, 2,
    3, 5, 4,
    0, 3, 4,
    0, 4, 1,
    1, 4, 5,
    1, 5, 2,
    2, 5, 3,
    2, 3, 0,
  ]);
  geometry.computeVertexNormals();
  return geometry;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'box-trailer';

  const body = surface('metal', 0x8c9299);
  const frame = surface('metal', 0x767c83);
  const vent = surface('metal', 0x2a2f33);
  const tyre = surface('organic', 0x1e2122);

  group.add(
    box([2.45, 0.12, 7.63], [0, 1.09, -0.125], frame, 'deck-frame'),
    box([2.45, 2.75, 7.63], [0, 2.525, -0.125], body, 'box-body'),
  );

  const ribs = new THREE.Group();
  ribs.name = 'side-ribs';
  // Ribs every 0.5 m give the long box its repetitive, cold industrial character.
  for (let index = 0; index < 16; index += 1) {
    for (const x of [-1.25, 1.25] as const) {
      ribs.add(
        box(
          [0.05, 2.7, 0.08],
          [x, 2.525, -3.96 + index * 0.5],
          frame,
          `rib-${x < 0 ? 'left' : 'right'}-${index + 1}`,
        ),
      );
    }
  }
  group.add(ribs);

  const vents = new THREE.Group();
  vents.name = 'ventilation-slots';
  // Dark recesses keep the unseen interior out of this beat; there are no implied occupants.
  for (let index = 0; index < 10; index += 1) {
    for (const x of [-1.219, 1.219] as const) {
      vents.add(
        box(
          [0.012, 0.06, 0.55],
          [x, 3.4, -3.375 + index * 0.75],
          vent,
          `vent-${x < 0 ? 'left' : 'right'}-${index + 1}`,
        ),
      );
    }
  }
  group.add(vents);

  const rear = new THREE.Group();
  rear.name = 'rear-doors';
  for (const [x, side] of [
    [-0.6375, 'left'],
    [0.6375, 'right'],
  ] as const) {
    const edgeX = x < 0 ? -1.2 : 1.2;
    rear.add(
      box([1.235, 2.6, 0.06], [x, 2.5, -3.97], body, `rear-door-${side}`),
      box([0.07, 2.7, 0.08], [edgeX, 2.5, -3.96], frame, `door-hinge-${side}`),
      box([0.06, 1.8, 0.08], [x, 2.25, -3.96], frame, `locking-bar-${side}`),
    );
  }
  rear.add(box([2.55, 0.12, 0.12], [0, 1.21, -3.94], frame, 'rear-sill'));
  group.add(rear);

  // Ending the core before the fairing prevents a coplanar front face from hiding the intended slope.
  group.add(box([2.45, 2.35, 0.06], [0, 2.325, 3.97], body, 'front-bulkhead'));
  group.add(mesh(frontNoseFairing(), body, 'front-nose-fairing'));

  const gear = new THREE.Group();
  gear.name = 'running-gear';
  for (const z of BOX_TRAILER_RUNNING_GEAR.wheels.axleZ) {
    for (const x of [-BOX_TRAILER_RUNNING_GEAR.wheels.centreX, BOX_TRAILER_RUNNING_GEAR.wheels.centreX] as const) {
      const wheel = mesh(
        new THREE.CylinderGeometry(BOX_TRAILER_RUNNING_GEAR.wheels.radius, BOX_TRAILER_RUNNING_GEAR.wheels.radius, BOX_TRAILER_RUNNING_GEAR.wheels.width, 12),
        tyre,
        `wheel-${z}-${x}`,
      );
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x, 0.5, z);
      gear.add(wheel);
    }
  }
  group.add(gear);

  for (const x of [-BOX_TRAILER_RUNNING_GEAR.legs.centreX, BOX_TRAILER_RUNNING_GEAR.legs.centreX] as const) {
    group.add(
      box(
        [BOX_TRAILER_RUNNING_GEAR.legs.section, BOX_TRAILER_RUNNING_GEAR.legs.top, BOX_TRAILER_RUNNING_GEAR.legs.section],
        [x, BOX_TRAILER_RUNNING_GEAR.legs.top / 2, BOX_TRAILER_RUNNING_GEAR.legs.z],
        frame,
        `landing-leg-${x < 0 ? 'left' : 'right'}`,
      ),
    );
  }
  group.add(box([0.85, 0.08, 0.65], [0, 1.05, 2.7], frame, 'kingpin-plate'));

  return group;
}

export const boxTrailerAsset: AssetDefinition = {
  id: 'box-trailer',
  label: 'Box trailer',
  description: 'Ribbed livestock box trailer for the loading race.',
  footprint: { width: 2.55, height: 3.9, length: 8 },
  create: build,
};
